import { IDatabaseTableModel, IDatasourceDto } from "@kuika/kuika-cl-model";
import * as React from "react";
import { IScreenDesignContextState } from "../../../../contexts/screen-design-context/screen-design-context";
import { KuikaDropdownMenuItemType } from "../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaEntitySwitcher, {
  KuikaEntitySwitcherItem
} from "../../../ui-kit/kuika-entity-switcher/kuika-entity-switcher";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import { getKnownTables } from "../datasource-create-actions";
import {
  canBrowseTableRows,
  canEditTable,
  canManageTables,
  deleteTableFromRow,
  isTableEditable
} from "../datasource-row-actions";
import { getNextTableName } from "../datasource-tree-actions";
import { getDatasourceDisplayName } from "../datasources-landing-model";
import { isSameTable } from "../table-editor-view";
import { getEditorGlyph, IEditorGlyph } from "./editor-glyph";
import { EditorGlyphIcon } from "./editor-glyph-icon";
import { buildTableSwitcherItems } from "./editor-switcher-items";
import { ISwitcherDelete, useSwitcherDelete } from "./use-switcher-delete";

/**
 * The identity chip on the table editor's bar: which table is open, and the way to the
 * other tables of the same datasource.
 *
 * The list comes from `databaseDiagramData.leftMenuData.tables` rather than `sd.schemas`:
 * the schema map is sometimes the slim one (`getTableIds` fills it with column-less rows),
 * and the tree opens tables from the loaded copy for the same reason.
 *
 * Its "…" menu offers what the datasources list offers a table row — Browse Data, and Delete
 * behind the same confirmation — through the same row actions, so the two cannot drift. The
 * confirmation lives here, beside the popover rather than inside it: the popover's content
 * unmounts when it closes, and a pick from the menu closes it.
 *
 * The editor has a Data tab, so the chip follows it: Browse Data on the open table only switches
 * the tab, and a table picked while Data is showing opens on Data.
 */

export interface ITableSwitcherChipProps {
  sd: IScreenDesignContextState;
  setNextCallbackFunction: (callback?: () => void) => void;
  datasource: IDatasourceDto | undefined;
  currentTable: IDatabaseTableModel | undefined;
  /** Schema-qualified, as the bar shows it: "dbo.Orders". */
  currentName: string;
  /** The editor's guarded close — it leads to the datasources list. */
  onManageAll: () => void;
  /** The editor is showing its Data tab: a table picked here opens on Data too. */
  isDataTabActive?: boolean;
  /** Browse Data on the open table: nothing is left, the editor only switches to its Data tab. */
  onBrowseCurrent?: () => void;
}

export const TableSwitcherChip = ({
  sd,
  setNextCallbackFunction,
  datasource,
  currentTable,
  currentName,
  onManageAll,
  isDataTabActive,
  onBrowseCurrent
}: ITableSwitcherChipProps): React.ReactElement => {
  const { deleteOption, deleteModal }: ISwitcherDelete<IDatabaseTableModel> = useSwitcherDelete(
    "table",
    (table: IDatabaseTableModel): void => void deleteTableFromRow(sd, table)
  );

  const source: IDatabaseTableModel[] = getKnownTables(sd, datasource);
  const items: KuikaEntitySwitcherItem[] = buildTableSwitcherItems(source);

  const find = (id: string): IDatabaseTableModel | undefined =>
    source.find((table: IDatabaseTableModel): boolean => (table.id || table.name) === id);

  // The datasources list's own rules for a table row.
  const mayManage: boolean = canManageTables(sd);
  const glyph: IEditorGlyph = getEditorGlyph({
    kind: "table",
    datasourceName: datasource ? getDatasourceDisplayName(datasource) : undefined
  });

  const getRowActions = (item: KuikaEntitySwitcherItem): KuikaDropdownMenuItemType[] => {
    const table: IDatabaseTableModel | undefined = find(item.id);
    if (!table || !datasource) return [];
    return [
      {
        key: "browse",
        label: "Browse Data",
        icon: <KuikaSvg name="table" width={16} height={16} />,
        hidden: !mayManage || !canBrowseTableRows(datasource),
        onClick: (): void => {
          if (onBrowseCurrent && currentTable && isSameTable(table, currentTable)) {
            onBrowseCurrent();
            return;
          }
          // Parked like a switch: browsing another table leaves this editor, so its guard gets a say first.
          setNextCallbackFunction((): void =>
            sd.setBrowsingRowsTable(datasource, { name: table.name, schema: table.schema })
          );
        }
      },
      deleteOption(table, !mayManage || !canEditTable(datasource) || !isTableEditable(sd, table))
    ];
  };

  return (
    <>
      <KuikaEntitySwitcher
        trigger="chip"
        triggerIcon={<EditorGlyphIcon glyph={glyph} />}
        triggerIconTooltip={glyph.label}
        triggerLabel={currentName}
        triggerTestId="tableSwitcherTrigger"
        // The suite reads the schema-qualified name here; it used to resolve `.header-name`.
        triggerLabelTestId="tableHeaderName"
        items={items}
        current={currentTable ? { id: currentTable.id || currentTable.name, name: currentName } : undefined}
        entityLabel="Table"
        iconName="table"
        getRowActions={getRowActions}
        onManageAll={onManageAll}
        onSelect={(item: KuikaEntitySwitcherItem): void => {
          const table: IDatabaseTableModel | undefined = find(item.id);
          if (!table || !datasource) return;
          setNextCallbackFunction((): void => {
            // The tab the user is on comes along. Browse Data routes to the table's editor on Data.
            if (isDataTabActive) {
              sd.setBrowsingRowsTable(datasource, table);
              return;
            }
            // Read-only outside the project's schema, as the tree's Edit and the datasources list open it.
            sd.setEditingTablesDatasource(datasource, table, false, undefined, !isTableEditable(sd, table));
          });
        }}
        onCreateNew={
          mayManage
            ? (): void => {
                if (!datasource) return;
                // Parked like a switch: the new table replaces this editor, so its guard gets a say first.
                setNextCallbackFunction((): void =>
                  sd.setEditingTablesDatasource(datasource, undefined, true, getNextTableName(sd, datasource))
                );
              }
            : undefined
        }
      />
      {deleteModal}
    </>
  );
};
